import { useMemo } from "react";
import { useThemeSlice, StatusBadge } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { DeleteConfirmModal } from "../../../components/modal/DeleteConfirmModal.jsx";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm.js";
import { buildAccountDetailTokens, detailActionBtnStyle } from "../../Accounts/accountDetailTheme.js";
import { buildSimDetailFields, buildSimDetailRows } from "../simUtils.js";

const ICON_MAP = {
  sim: Ico.sim,
  device: Ico.device,
  building: Ico.building,
  layers: Ico.layers,
  model: Ico.model,
  grid: Ico.grid,
};

export function SimDetailPanel({ sim, simType, onEdit, onDelete }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  if (!sim) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: c.sub, fontSize: 13, background: c.bg }}>
        Select a SIM card to view details
      </div>
    );
  }

  const detailFields = buildSimDetailFields(sim, simType);
  const detailRows = buildSimDetailRows(detailFields);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, background: c.bg }}>
      <div style={{ flex: 1, overflow: "auto", padding: "14px 16px 16px", minHeight: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", padding: "12px 16px", borderRadius: 10, background: c.hover, border: `1px solid ${c.cardBdr}`, marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.sub, marginBottom: 3 }}>ICCID</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all" }}>{sim.iccid}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <StatusBadge label={sim.status} colors={c} accent={A} />
            <button type="button" onClick={() => onEdit(sim)} style={detailActionBtnStyle(ap)} aria-label="Edit SIM">{Ico.pencil}</button>
            <button
              type="button"
              onClick={() => requestDelete({
                title: "Delete SIM",
                description: "This SIM card will be permanently removed from inventory.",
                confirmText: sim.iccid,
                entityLabel: "ICCID",
                confirmLabel: "Delete SIM",
                onConfirm: () => onDelete(sim.id),
              })}
              style={detailActionBtnStyle(ap, "danger")}
              aria-label="Delete SIM"
            >
              {Ico.trash}
            </button>
          </div>
        </div>

        <div style={{ background: ap.card, border: `1px solid ${ap.cardBorder}`, borderRadius: 10, overflow: "hidden" }}>
          {detailRows.map((row, rowIdx) => (
            <div key={rowIdx} style={{ display: "grid", gridTemplateColumns: row.length === 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", borderTop: rowIdx > 0 ? `1px solid ${ap.cardBorder}` : undefined }}>
              {row.map((field, colIdx) => (
                <SimFieldCell key={field.label} field={field} c={c} ap={ap} showLeftBorder={colIdx > 0 && row.length > 1} />
              ))}
            </div>
          ))}
        </div>
      </div>
      <DeleteConfirmModal {...deleteModalProps} />
    </div>
  );
}

function SimFieldCell({ field, c, ap, showLeftBorder }) {
  const icon = ICON_MAP[field.icon] ?? Ico.sim;
  return (
    <div style={{ borderLeft: showLeftBorder ? `1px solid ${ap.cardBorder}` : undefined }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", minWidth: 0 }}>
        <div style={{ width: 26, height: 26, borderRadius: 6, background: ap.fieldIconBg, border: `1px solid ${ap.fieldIconBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: ap.fieldIconColor, flexShrink: 0 }}>{icon}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: ap.fieldLabel, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{field.label}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.text, wordBreak: "break-word", lineHeight: 1.35 }}>{field.value || "—"}</div>
        </div>
      </div>
    </div>
  );
}
